"use client";

import { StyleProvider } from "@ant-design/cssinjs";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import {
  isServer,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { ConfigProvider } from "antd";
import { ANT_CUSTOM_THEME } from "configs/AntThemeConfig";
import mixpanel from "mixpanel-browser";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import { Routes } from "services/constants/RouteConstants";
import { insertSpaces } from "utils/Analytics/Analytics";
import { Analytics } from "utils/Analytics/AnalyticsHelper";
import config from "utils/config";
import { setCookie } from "utils/cookie-manager";
import zipy from "zipyai";

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  });
};

let browserQueryClient: QueryClient | undefined = undefined;

const tagManagerArgs = {
  gtmId: config?.GTM_ID,
};

const ZIPY_KEY = config.ZIPPY_KEY ?? "";

const getQueryClient = () => {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
};

const Providers = ({ children }) => {
  const pathname = usePathname();
  const queryClient = getQueryClient();
  const GOOGLE_ANALYTICS_ID = config.GA_ID ?? "";
  ReactGA.initialize(GOOGLE_ANALYTICS_ID);

  const storeUTMParameters = (): void => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmCookies = [
      "utm_source",
      "utm_page",
      "utm_medium",
      "utm_campaign",
      "utm_user",
      "http_referer",
      "utm_content",
      "utm_term",
    ];
    if (urlParams.get("http_referrer")) {
      setCookie("http_referrer", urlParams.get("http_referrer") || "");
    }
    utmCookies.forEach((name) => {
      if (urlParams.get(name)) {
        setCookie(name, urlParams.get(name) || "");
      }
    });
  };

  const logPageLoadEvents = (): void => {
    var match: any = null;
    match = Routes.find((route) => route.pattern === location?.pathname);
    if (!match) {
      match = Routes.find((route) => route.pattern === pathname);
    }
    if (!match) {
      match = { name: "NA" };
    }
    const currentPage: string = match?.name ? insertSpaces(match?.name) : "";
    ReactGA.set({ page: location?.pathname }); // Update the user's current page
    // ReactGA.pageview(window.location.pathname);
    if (match && currentPage !== "NA") {
      ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname,
        title: currentPage,
      });
      Analytics.page(currentPage);
    }
    // zipy initialization
    if (config.IS_PROD === "true") {
      if (
        [
          "Public Profile",
          "Public Homepage",
          "Pricing",
          "Sign Up",
          "Login",
          "Privacy",
          "About",
          "Video Services",
          "Webinar Services",
          "Package Services",
          "Query Services",
          "Document Services",
          "Upcoming Video Calls",
          "Upcoming Webinar Calls",
          "Upcoming Package Calls",
          "Service Page",
          "Video Call Ended",
          "Testimonial Success",
          "Rewards",
          "Onboarding About",
        ]?.includes(currentPage)
      ) {
        Math.random() < 0.1 && zipy.init(ZIPY_KEY);
      } else {
        Math.random() < 0.5 && zipy.init(ZIPY_KEY);
      }
    }
  };

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
    // Note: This is configured to track an event for every page view automatically. We also recommend
    // using localStorage instead of the default cookie for persistence.
    mixpanel.init(config.MIXPANEL_TOKEN, {
      debug: config.IS_PROD === "true" ? false : true,
      persistence: "localStorage",
    });

    setTimeout(() => {
      storeUTMParameters();
      logPageLoadEvents();
      if (
        window?.localStorage &&
        window?.localStorage?.getItem("queryClientCache")
      ) {
        localStorage?.removeItem("queryClientCache");
      }
    });
  }, []);

  useEffect(() => {
    storeUTMParameters();
    logPageLoadEvents();
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <AntdRegistry>
        <ConfigProvider theme={ANT_CUSTOM_THEME}>
          <StyleProvider layer>{children}</StyleProvider>
          <SpeedInsights sampleRate={0.05} />
        </ConfigProvider>{" "}
      </AntdRegistry>
      {config.IS_PROD === "false" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

export default Providers;
